import {
  Row,
  Col,
  Card,
  Statistic,
  Tag,
  Button,
  message,
  Tooltip,
  Grid,
} from "antd";
import PieChart from "./graphs/PieChart";
import ColumnChart from "./graphs/ColumnChart";
import { useAPI } from "../../hooks/useAPI";
import { ENDPOINTS } from "../../constants/endpoints.constant";
import { getCall } from "../../constants/apicalls.constant";
import Loader from "../../components/Loader";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import TableChart from "./graphs/TableChart";
import { IconCoins, IconLink, IconTicket } from "@tabler/icons-react";
import { useParams } from "react-router";
import Container from "../../components/container";

import getCurrencyWithSymbol from "../../utils/getCurrencySymbol";
import ShareEvent from "../../utils/QrCodeCustom";
import { canDownloadEventQr } from "../../utils/eventEditAccess";
import { hasChartRecordData } from "../../utils/chartData";

const Dashboard = () => {
  const event = useSelector((state: RootState) => state.event);
  const { id: routeEventId } = useParams();
  const eventId = routeEventId ?? event.id;

  const { response, loading, error } = useAPI<{
    message: string;
    status: boolean;
    data: {
      total_revenue: string;
      total_registrations: string;
      total_revenue_by_source: {
        total: string;
        sources: Record<string, string>;
      };
      total_checkins: string;
      tickets_by_type: Record<string, string> | [] | null;
      registrations_by_date: Record<string, string> | [] | null;
      recent_registrations: {
        first_name: string;
        last_name: string;
        phone: string;
        ticket_name: string;
        quantity: string;
      }[];
    };
  }>(
    ENDPOINTS.dashboard,
    !!eventId,
    [eventId],
    eventId ? getCall({ event_id: eventId }) : undefined,
  );

  const url = `${import.meta.env.VITE_CUSTOMER_URL}/events/${event.slug}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      message.success("URL copied to clipboard!");
    } catch (err: unknown) {
      message.error(
        "Failed to copy URL. " +
          (err instanceof Error ? err.message : String(err)),
      );
    }
  };
  const screen = Grid.useBreakpoint();
  const showQrDownload = canDownloadEventQr(event);

  const totalRegs = response?.data?.total_registrations;
  const totalRegsNum = Number(totalRegs);
  const hasRegs = Number.isFinite(totalRegsNum) && totalRegsNum > 0;
  const revenueTotal = response?.data?.total_revenue_by_source?.total;
  const revenueNum = Number(revenueTotal);
  const hasRevenue = Number.isFinite(revenueNum) && revenueNum > 0;
  const hasTicketChart = hasChartRecordData(response?.data?.tickets_by_type);
  const hasDateChart = hasChartRecordData(
    response?.data?.registrations_by_date,
  );

  if (loading) return <Loader />;
  if (error) return <>something went wrong</>;
  return (
    <Container>
      <div className="!py-[24px]">
        <div className="flex flex-wrap items-start justify-between gap-[12px] mb-[20px]">
          <div>
            <h2 className="ds-page-header-title ff-title !text-xl">
              Dashboard
            </h2>
            <p className="ds-page-header-desc">
              An overview of your event performance
            </p>
          </div>
          <div className="flex items-center gap-[12px]">
            {screen.md ? (
              <div className="flex items-center gap-[8px] border border-gray-200 bg-gray-50 rounded-[8px] px-[12px] py-[6px] max-w-[480px] w-full">
                <span className="flex-1 text-[12px] text-gray-400 overflow-hidden text-ellipsis whitespace-nowrap">
                  {url}
                </span>
                <Button
                  size="small"
                  icon={<IconLink size={14} />}
                  className="!bg-[#111827] !text-white !border-none !rounded-[6px] shrink-0"
                  onClick={handleCopy}
                >
                  Copy
                </Button>
              </div>
            ) : (
              <Tooltip title="Copy event link">
                <Button
                  size="small"
                  icon={<IconLink size={14} />}
                  className="!bg-[#111827] !text-white !border-none !rounded-[6px]"
                  onClick={handleCopy}
                />
              </Tooltip>
            )}
            {showQrDownload && <ShareEvent url={url} event={event} />}
          </div>
        </div>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} lg={8}>
            <Card>
              <Statistic
                title={<span className="text-gray">Total Registration</span>}
                value={hasRegs ? totalRegs : "—"}
                precision={0}
                valueStyle={{ color: hasRegs ? "var(--color-black)" : "#d1d5db" }}
                prefix={
                  <Tag bordered={false} style={{ background: "rgba(121, 201, 255, 0.2)", color: "#79C9FF" }}>
                    <IconTicket size={20} />
                  </Tag>
                }
                prefixCls="dashboard-stats"
              />
              {!hasRegs && (
                <p style={{ margin: "4px 0 0", fontSize: 11, color: "#9ca3af" }}>
                  No registrations recorded for this event
                </p>
              )}
            </Card>
          </Col>

          <Col xs={24} sm={12} lg={16}>
            <Card>
              <Statistic
                title={<span className="text-gray">Total Revenue</span>}
                value={hasRevenue ? getCurrencyWithSymbol("INR", revenueNum) : "—"}
                precision={0}
                valueStyle={{ color: hasRevenue ? "var(--color-black)" : "#d1d5db" }}
                prefix={
                  <Tag bordered={false} style={{ background: "rgba(255, 184, 122, 0.2)", color: "#FFB87A" }}>
                    <IconCoins size={20} />
                  </Tag>
                }
                prefixCls="dashboard-stats"
              />
              {!hasRevenue && (
                <p style={{ margin: "4px 0 0", fontSize: 11, color: "#9ca3af" }}>
                  Revenue will show once payments are processed
                </p>
              )}
            </Card>
          </Col>
          <Col xs={24} sm={24} lg={9}>
            <Card title="Ticket Sales" className="h-full">
              <PieChart data={response?.data?.tickets_by_type ?? null} />
              {hasRegs && !hasTicketChart && (
                <p style={{ margin: "8px 0 0", fontSize: 11, color: "#9ca3af", textAlign: "center" }}>
                  Ticket breakdown is not available for this event
                </p>
              )}
            </Card>
          </Col>
          <Col xs={24} sm={24} lg={15}>
            <Card title="Recent Registrations">
              {response ? (
                <TableChart data={response.data.recent_registrations!} />
              ) : (
                <></>
              )}
            </Card>
          </Col>
          <Col xs={24} sm={24}>
            <Card title="Tickets sold over days">
              <ColumnChart
                data={response?.data?.registrations_by_date ?? null}
              />
              {hasRegs && !hasDateChart && (
                <p style={{ margin: "8px 0 0", fontSize: 11, color: "#9ca3af", textAlign: "center" }}>
                  Daily sales chart is not available for this event
                </p>
              )}
            </Card>
          </Col>
        </Row>
      </div>
    </Container>
  );
};

export default Dashboard;
